import React, { useState, useEffect } from 'react';

const DashboardAgreementsSection = ({
  hasAcceptedTerms,
  hasAcceptedSms,
  hasTradingConfig,
  canAccessChannelConfig,
  onOpenTerms,
  onOpenSms,
  onSkipSms,
  onOpenTradingConfig,
  /** Stable identifier (e.g. user email) used to namespace the localStorage key. */
  userKey = '',
  /** Shown under Step 1 when terms need re-signing (e.g. 90-day policy). */
  termsReminderMessage = '',
  /** Shown under Step 2 when SMS needs re-signing. */
  smsReminderMessage = '',
  /** When false, Step 3 omits the in-card button (e.g. mobile uses top nav → Channel Config). */
  showStep3AddTradingButton = true,
}) => {
  const storageKey = userKey ? `sms_step_dismissed_${userKey}` : null;

  const [smsDismissed, setSmsDismissed] = useState(() => {
    if (!storageKey) return false;
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch {
      return false;
    }
  });

  // Clear the dismissed flag as soon as the user actually opts in —
  // so if they ever revoke consent it shows again correctly.
  useEffect(() => {
    if (hasAcceptedSms && storageKey) {
      try {
        localStorage.removeItem(storageKey);
      } catch {
        // ignore
      }
    }
  }, [hasAcceptedSms, storageKey]);

  const handleSkipSms = () => {
    setSmsDismissed(true);
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, 'true');
      } catch {
        // ignore — storage quota or private-mode restriction
      }
    }
    if (typeof onSkipSms === 'function') onSkipSms();
  };
  const termsCardClasses = hasAcceptedTerms
    ? 'bg-green-50 border-green-200'
    : 'bg-yellow-50 border-yellow-200';

  // SMS is optional — card is always neutral blue, never blocking yellow.
  const smsBlockedByTerms = !hasAcceptedTerms;
  const smsCardClasses = hasAcceptedSms
    ? 'bg-green-50 border-green-200'
    : 'bg-blue-50 border-blue-200';

  // Step 3 only requires Terms (Step 1). SMS is optional.
  const tradingBlockedByAgreements = !hasAcceptedTerms;
  const tradingCardClasses = hasTradingConfig
    ? 'bg-green-50 border-green-200'
    : tradingBlockedByAgreements
      ? 'bg-yellow-50 border-yellow-200'
      : 'bg-blue-50 border-blue-200';

  const showSetupBanner = !hasAcceptedTerms || !hasTradingConfig;

  return (
    <div className="mt-8 space-y-4">
      {showSetupBanner && (
        <p className="text-base text-gray-700 font-medium mb-4">
          Complete the required steps below to start using the app. Step 2 (SMS) is optional and can
          be skipped or completed later from Account Settings.
        </p>
      )}

      {/* Step 1 — Terms (required) */}
      <div className={`p-6 rounded-xl border ${termsCardClasses}`}>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Step 1: Terms and Conditions</h3>
        {termsReminderMessage ? (
          <p className="text-sm text-amber-800 mb-2">{termsReminderMessage}</p>
        ) : null}
        <p className="text-sm text-gray-700 mb-4">
          {hasAcceptedTerms
            ? 'You have accepted the latest terms and conditions.'
            : 'Please review and accept the terms and conditions first.'}
        </p>
        {!hasAcceptedTerms && (
          <button
            type="button"
            onClick={onOpenTerms}
            className="inline-flex items-center px-4 py-2.5 rounded-lg bg-yellow-600 text-white text-sm font-medium hover:bg-yellow-700 min-h-[44px]"
          >
            Review &amp; Accept Terms
          </button>
        )}
      </div>

      {/* Step 2 — SMS consent (optional) */}
      <div className={`p-6 rounded-xl border ${smsDismissed && !hasAcceptedSms ? 'bg-gray-50 border-gray-200' : smsCardClasses}`}>
        <div className="flex items-start justify-between mb-2 gap-2">
          <h3 className="text-lg font-semibold text-gray-900">Step 2: SMS Notifications Consent</h3>
          {!hasAcceptedSms && (
            <span className="shrink-0 inline-block rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700">
              Optional — Recommended
            </span>
          )}
        </div>
        {smsReminderMessage ? (
          <p className="text-sm text-amber-800 mb-2">{smsReminderMessage}</p>
        ) : null}
        <p className="text-sm text-gray-700 mb-4">
          {smsBlockedByTerms
            ? 'Complete Step 1 (Terms) first, then you can opt in to SMS consent.'
            : hasAcceptedSms
              ? 'You have opted in to SMS notifications. Trade alerts and reminders will be sent via text.'
              : smsDismissed
                ? 'Skipped — you can opt in to SMS alerts any time from Account Settings.'
                : 'Opt in to receive important trading alerts and auto-approve reminders by SMS. You can skip this now and opt in later from Account Settings.'}
        </p>
        {!hasAcceptedSms && !smsBlockedByTerms && !smsDismissed && (
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onOpenSms}
              className="inline-flex items-center px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 min-h-[44px]"
            >
              Opt in to SMS
            </button>
            <button
              type="button"
              onClick={handleSkipSms}
              className="inline-flex items-center px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 min-h-[44px]"
            >
              Skip for now
            </button>
          </div>
        )}
      </div>

      {/* Step 3 — Trading configuration (unlocked by Terms only) */}
      <div className={`p-6 rounded-xl border ${tradingCardClasses}`}>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Step 3: Trading Configuration</h3>
        <p className="text-sm text-gray-700 mb-4">
          {tradingBlockedByAgreements
            ? 'Complete Step 1 (Terms) first, then add your channel mapping and risk settings.'
            : hasTradingConfig
              ? 'Your trading configuration is set. You can update risk settings at any time from Channel Config.'
              : showStep3AddTradingButton
                ? 'Add your Tradier API token, Account ID, Discord user ID, and risk parameters.'
                : 'Add your trading configuration from Channel Config in the top menu.'}
        </p>
        {!hasTradingConfig && canAccessChannelConfig && !tradingBlockedByAgreements && showStep3AddTradingButton && (
          <button
            type="button"
            onClick={onOpenTradingConfig}
            className="inline-flex items-center px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 min-h-[44px]"
          >
            Add Trading Configuration
          </button>
        )}
      </div>
    </div>
  );
};

export default DashboardAgreementsSection;
